// Ideas (brief update 2, sections 8 and 21; docs/PLAN.md step 4). Storing an idea starts no work.
// The personal page lists every idea the person may see (their own without a workspace, and those of
// their workspaces); a workspace page lists that workspace's. Converting one into a task or project is
// the founder's explicit act.
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useContextWorkspace } from '../components/Layout';
import { Audience, Chip, Demo, Empty, ErrorBox, Field, Loading, Modal, PageHead } from '../components/ui';
import { api, errorText } from '../lib/api';
import { day } from '../lib/format';
import { useSession } from '../lib/session';
import type { Idea, Project } from '../lib/types';
import { useApi } from '../lib/useApi';

const GROUPS: { state: string; title: string; hint: string }[] = [
  { state: 'ready_to_revisit', title: 'Ready to revisit', hint: 'Marked to come back to.' },
  { state: 'exploring', title: 'Exploring', hint: 'Being thought through; nothing is committed.' },
  { state: 'stored', title: 'Stored', hint: 'Kept for later. Storing starts no work.' },
  { state: 'converted', title: 'Converted', hint: 'Became an approved task or project.' },
  { state: 'archived', title: 'Archived', hint: 'Set aside.' },
];

export function Ideas() {
  const ws = useContextWorkspace();
  const [params, setParams] = useSearchParams();
  const ideas = useApi<Idea[]>(`/api/ideas?scope=${ws ? ws.slug : 'personal'}`);
  const openId = params.get('open');
  const open = ideas.data?.find((i) => i.id === openId) ?? null;

  const replace = (i: Idea) => {
    if (ideas.data) ideas.setData(ideas.data.map((x) => (x.id === i.id ? i : x)));
    void ideas.reload();
  };

  return (
    <>
      <PageHead
        title={ws ? `${ws.name} ideas` : 'My ideas'}
        sub={
          ws
            ? `Ideas kept in ${ws.name}. Storing one never starts work.`
            : 'Your ideas, and those of your workspaces. Storing one never starts work.'
        }
      />
      <section className="panel">
        <h2>Capture an idea</h2>
        <CaptureForm onStored={() => void ideas.reload()} />
      </section>
      {ideas.error && <ErrorBox error={ideas.error} retry={ideas.reload} />}
      {!ideas.data && !ideas.error && <Loading what="Loading ideas" />}
      {ideas.data && !ideas.data.length && <Empty>No ideas yet.</Empty>}
      {ideas.data &&
        GROUPS.map((g) => {
          const list = ideas.data!.filter((i) => i.state === g.state);
          if (!list.length) return null;
          return (
            <section className="panel" key={g.state} aria-label={g.title}>
              <h2>{g.title}</h2>
              <p className="muted small">{g.hint}</p>
              <ul className="recs">
                {list.map((i) => (
                  <IdeaRow key={i.id} idea={i} showWorkspace={!ws} onOpen={() => setParams({ open: i.id })} />
                ))}
              </ul>
            </section>
          );
        })}
      {open && <IdeaSheet idea={open} onClose={() => setParams({})} onChanged={replace} />}
    </>
  );
}

export function IdeaRow({ idea, showWorkspace, onOpen }: { idea: Idea; showWorkspace?: boolean; onOpen: () => void }) {
  return (
    <li className="rec idea" id={`idea-${idea.id}`}>
      <div className="rec-main">
        <button type="button" className="link rec-title" onClick={onOpen}>
          {idea.title}
        </button>
        <div className="rec-meta">
          <Chip value={idea.state} />
          <Audience item={idea} />
          <Demo show={idea.demo} />
          {showWorkspace && <span className="muted small">{idea.workspace ?? 'not in a workspace yet'}</span>}
          {idea.project && <span className="muted small">project: {idea.project.title}</span>}
        </div>
        {idea.revisit_reason && <p className="small revisit">{idea.revisit_reason}</p>}
      </div>
    </li>
  );
}

/** One text box: the idea is stored where you are (personal: no workspace yet). */
export function CaptureForm({ onStored }: { onStored: (i: Idea) => void }) {
  const ws = useContextWorkspace();
  const [text, setText] = useState('');
  const [shared, setShared] = useState(false);
  const [revisitOn, setRevisitOn] = useState('');
  const [error, setError] = useState('');
  const [stored, setStored] = useState<Idea | null>(null);

  async function store(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    try {
      const i = await api<Idea>('/api/ideas', {
        body: { scope: ws ? ws.slug : 'personal', text, private: !shared, revisit_on: revisitOn || undefined },
      });
      setText('');
      setRevisitOn('');
      setStored(i);
      onStored(i);
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <form onSubmit={store} className="capture" aria-label="Capture an idea">
      <Field label="What is the idea?" hint="Kept word for word. Dictation arrives once a transcription service is chosen.">
        <textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} maxLength={20_000} required name="idea" />
      </Field>
      <div className="row gap wrap">
        {ws && (
          <label className="check">
            <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} /> Share with everyone in {ws.name}
          </label>
        )}
        <Field label="Revisit on (optional)">
          <input type="date" value={revisitOn} onChange={(e) => setRevisitOn(e.target.value)} />
        </Field>
      </div>
      {error && <p className="msg err">{error}</p>}
      {stored && (
        <p className="msg ok" role="status">
          Stored “{stored.title}”: {stored.seen_by.toLowerCase()}. No work was started.
        </p>
      )}
      <button type="submit" className="btn primary" disabled={!text.trim()}>
        Store the idea
      </button>
    </form>
  );
}

function IdeaSheet({ idea, onClose, onChanged }: { idea: Idea; onClose: () => void; onChanged: (i: Idea) => void }) {
  const { me } = useSession();
  const [form, setForm] = useState({
    title: idea.title,
    interpretation: idea.interpretation,
    revisit_condition: idea.revisit_condition,
    revisit_on: idea.revisit_on ?? '',
    project: idea.project?.id ?? '',
  });
  const [moveTo, setMoveTo] = useState('');
  const [error, setError] = useState('');
  const [made, setMade] = useState<{ type: string; id: string } | null>(null);
  const projects = useApi<Project[]>(idea.workspace_slug ? `/api/w/${idea.workspace_slug}/projects` : null);
  useEffect(() => setMade(null), [idea.id]);
  const converted = idea.state === 'converted';

  async function patch(body: Record<string, unknown>) {
    setError('');
    try {
      onChanged(await api<Idea>(`/api/ideas/${idea.id}`, { method: 'PATCH', body }));
    } catch (err) {
      setError(errorText(err));
    }
  }

  async function convert(into: 'task' | 'project') {
    setError('');
    try {
      const out = await api<Idea & { made: { type: string; id: string } }>(`/api/ideas/${idea.id}/convert`, { body: { into } });
      setMade(out.made);
      onChanged(out);
    } catch (err) {
      setError(errorText(err));
    }
  }

  const result = made ?? (idea.converted_task_id ? { type: 'task', id: idea.converted_task_id } : idea.converted_project_id ? { type: 'project', id: idea.converted_project_id } : null);
  const resultHref = result && idea.workspace_slug
    ? result.type === 'task'
      ? `/w/${idea.workspace_slug}/projects?tab=tasks&open=${result.id}`
      : `/w/${idea.workspace_slug}/projects/${result.id}`
    : null;

  return (
    <Modal title={idea.title} onClose={onClose} wide>
      <div className="row gap wrap">
        <Chip value={idea.state} />
        <Audience item={idea} />
        <Demo show={idea.demo} />
        <span className="muted small">
          {idea.workspace ?? 'Not in a workspace yet'} · {idea.created_by ?? 'someone'}, {day(idea.created_at)}
        </span>
      </div>
      {idea.revisit_reason && <p className="small revisit">{idea.revisit_reason}</p>}
      <h3>Original note</h3>
      <p className="pre quote">{idea.original_text}</p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void patch({
            title: form.title,
            interpretation: form.interpretation,
            revisit_condition: form.revisit_condition,
            revisit_on: form.revisit_on || undefined,
            clear_revisit_on: !form.revisit_on,
            project_id: form.project || undefined,
            clear_project: !form.project && Boolean(idea.project),
          });
        }}
      >
        <Field label="Title">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={200} required />
        </Field>
        <Field label="Interpretation" hint="What it means, in a sentence or two. The original note above never changes.">
          <textarea rows={3} value={form.interpretation} onChange={(e) => setForm({ ...form, interpretation: e.target.value })} />
        </Field>
        <Field label="Come back to it when…">
          <input value={form.revisit_condition} onChange={(e) => setForm({ ...form, revisit_condition: e.target.value })} maxLength={2000} />
        </Field>
        <div className="row gap wrap">
          <Field label="Revisit on">
            <input type="date" value={form.revisit_on} onChange={(e) => setForm({ ...form, revisit_on: e.target.value })} />
          </Field>
          {idea.workspace_slug && (
            <Field label="Related project">
              <select value={form.project} onChange={(e) => setForm({ ...form, project: e.target.value })}>
                <option value="">None</option>
                {projects.data?.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title}
                    {p.private ? ' (private)' : ''}
                  </option>
                ))}
              </select>
            </Field>
          )}
        </div>
        <button type="submit" className="btn">
          Save
        </button>
      </form>

      {!converted && (
        <section aria-label="What happens next">
          <h3>What happens next</h3>
          <div className="row gap wrap">
            {idea.state !== 'exploring' && (
              <button type="button" className="btn small" onClick={() => void patch({ state: 'exploring' })}>
                Explore it
              </button>
            )}
            {idea.state !== 'stored' && (
              <button type="button" className="btn small" onClick={() => void patch({ state: 'stored' })}>
                Store it
              </button>
            )}
            {idea.state !== 'ready_to_revisit' && (
              <button type="button" className="btn small" onClick={() => void patch({ state: 'ready_to_revisit' })}>
                Ready to revisit
              </button>
            )}
            {idea.state !== 'archived' && (
              <button type="button" className="btn small ghost" onClick={() => void patch({ state: 'archived' })}>
                Archive
              </button>
            )}
          </div>
          {!idea.workspace_slug ? (
            <div className="row gap wrap">
              <Field label="Put it in a workspace" hint="It stays private to you there until you share it.">
                <select value={moveTo} onChange={(e) => setMoveTo(e.target.value)}>
                  <option value="">Choose…</option>
                  {me.workspaces.map((w) => (
                    <option key={w.slug} value={w.slug}>
                      {w.name}
                    </option>
                  ))}
                </select>
              </Field>
              <button type="button" className="btn small" disabled={!moveTo} onClick={() => void patch({ move_to: moveTo })}>
                Move
              </button>
            </div>
          ) : (
            <div className="row gap wrap">
              {idea.private && (
                <button type="button" className="btn small" onClick={() => void patch({ publish: true })}>
                  Share with everyone in {idea.workspace}
                </button>
              )}
              <button type="button" className="btn small primary" onClick={() => void convert('task')}>
                Approve as a task
              </button>
              <button type="button" className="btn small" onClick={() => void convert('project')}>
                Start a project from it
              </button>
            </div>
          )}
          <p className="muted small">
            Approving creates the task or project in {idea.workspace ?? 'its workspace'}, {idea.private ? 'private to you' : 'shared'}, with you
            accountable. Nothing else starts by itself.
          </p>
        </section>
      )}
      {converted && result && (
        <p className="msg ok">
          Became a {result.type}
          {resultHref ? (
            <>
              : <Link to={resultHref}>open it</Link>
            </>
          ) : null}
          .
        </p>
      )}
      {error && <p className="msg err">{error}</p>}
    </Modal>
  );
}
